/**
 * ValuesDropZone Component
 *
 * Drop zone for values with sortable list and aggregation function selection.
 * Fields in values determine what is calculated/aggregated in the pivot table.
 */

import { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { TableFieldApiResponse, LinkedTableValueApi } from '@loader/shared-types';
import { parseFieldId } from '@loader/shared-types';
import { GripVertical, X, ChevronDown, Sigma } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@loader/ui';

import type { LinkedTableCalculatedColumn } from '@/lib/types';
import { findFieldById } from '@/features/linkedTables/editLinkedTable/utils/viewHelpers';

/**
 * Available aggregation functions for values
 */
const AGGREGATION_FUNCTIONS: Array<{
  value: LinkedTableValueApi['function'];
  label: string;
}> = [
  { value: 'count', label: 'count' },
  { value: 'sum', label: 'sum' },
  { value: 'avg', label: 'avg' },
  { value: 'min', label: 'min' },
  { value: 'max', label: 'max' },
];

/**
 * Props for ValuesDropZone
 */
interface ValuesDropZoneProps {
  /** Array of value configurations */
  values: LinkedTableValueApi[];
  /** All available fields for lookup */
  availableFields: TableFieldApiResponse[];
  /** Calculated columns for lookup */
  calculatedColumns?: LinkedTableCalculatedColumn[];
  /** Handler to remove a field from values */
  onRemove?: (fieldId: string) => void;
  /** Handler to reorder values */
  onReorder?: (values: LinkedTableValueApi[]) => void;
  /** Handler to update aggregation function */
  onUpdateFunction?: (fieldId: string, func: LinkedTableValueApi['function']) => void;
  /** Currently dragged item ID (for showing drop indicator) */
  activeId?: string | null;
  /** Item being hovered over (for showing drop indicator) */
  overId?: string | null;
  /** Whether editing is allowed (default: true) */
  canEdit?: boolean;
}

/**
 * Individual sortable value item
 */
interface SortableValueItemProps {
  value: LinkedTableValueApi;
  field: TableFieldApiResponse | undefined;
  onRemove?: (fieldId: string) => void;
  onUpdateFunction?: (fieldId: string, func: LinkedTableValueApi['function']) => void;
  canEdit?: boolean;
}

function SortableValueItem({
  value,
  field,
  onRemove,
  onUpdateFunction,
  canEdit = true,
}: SortableValueItemProps) {
  const [open, setOpen] = useState(false);
  // Use prefixed ID to avoid conflicts with column list items
  const sortableId = `value-${value.field_id}`;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sortableId,
    disabled: !canEdit,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Parse qualified ID for display fallback (show bare ID, not "alias:id")
  const displayName = field ? field.name : parseFieldId(value.field_id).fieldId;
  const isNumeric = field?.data_type === 'number';

  // Format as function(field)
  const formattedLabel = `${value.function}(${displayName})`;

  const handleSelectFunction = (func: LinkedTableValueApi['function']) => {
    onUpdateFunction?.(value.field_id, func);
    setOpen(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-1.5 rounded-lg bg-accent px-2 py-1.5"
    >
      {/* Drag Handle */}
      {canEdit && (
        <div
          className="cursor-grab active:cursor-grabbing shrink-0"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
      )}

      {/* Function Label with Popover */}
      {canEdit ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex-1 flex items-center gap-1 text-sm text-left hover:bg-accent rounded px-1 py-0.5 min-w-0"
              title="Click to change aggregation function"
            >
              <span className="truncate">{formattedLabel}</span>
              <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-28 p-1" align="start">
            <div className="flex flex-col">
              {AGGREGATION_FUNCTIONS.map((func) => {
                // Disable sum/avg for non-numeric fields
                const disabled = !isNumeric && ['sum', 'avg'].includes(func.value);
                const isSelected = value.function === func.value;

                return (
                  <button
                    key={func.value}
                    type="button"
                    disabled={disabled}
                    onClick={() => handleSelectFunction(func.value)}
                    className={`
                      text-left px-2 py-1.5 text-sm rounded-sm
                      ${isSelected ? 'bg-accent' : 'hover:bg-accent/50'}
                      ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    {func.label}
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      ) : (
        <span className="flex-1 text-sm truncate px-1 py-0.5">{formattedLabel}</span>
      )}

      {/* Remove Button */}
      {canEdit && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove?.(value.field_id);
          }}
          onPointerDown={(e) => e.stopPropagation()}
          className="h-5 w-5 flex items-center justify-center rounded hover:bg-background shrink-0 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

/**
 * Drop placeholder component
 */
function DropPlaceholder() {
  return (
    <div className="h-8 rounded-lg border border-dashed border-primary bg-primary/5 flex items-center justify-center">
      <span className="text-xs text-primary">Drop here</span>
    </div>
  );
}

/**
 * ValuesDropZone Component
 */
export function ValuesDropZone({
  values,
  availableFields,
  calculatedColumns = [],
  onRemove,
  onUpdateFunction,
  activeId,
  overId,
  canEdit = true,
}: ValuesDropZoneProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'values-zone',
  });

  // Create sortable items from field_ids with prefix to avoid ID conflicts
  const sortableIds = values.map((v) => `value-${v.field_id}`);

  // Determine if we should show the drop placeholder
  // Show when dragging from outside (column list or row-groups) and hovering over this zone
  const isFromOutside = activeId && !activeId.startsWith('value-');
  const isOverThisZone = overId === 'values-zone' || (overId?.startsWith('value-') ?? false);
  const showPlaceholder = isFromOutside && isOverThisZone;

  // Helper to find field by ID (supports qualified "alias:fieldId" format)
  const lookupField = (fieldId: string) => findFieldById(fieldId, availableFields, calculatedColumns);

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <Sigma className="h-4 w-4" />
        <span>Values</span>
      </div>

      {/* Drop Zone */}
      <div
        ref={setNodeRef}
        className={`
          min-h-[36px] rounded-lg p-1.5 border border-dashed
          transition-colors
          ${isOver ? 'bg-primary/10 border-primary' : 'border-border'}
        `}
      >
        {values.length === 0 ? (
          showPlaceholder ? (
            <DropPlaceholder />
          ) : (
            <div className="flex items-center justify-center h-8 text-sm text-muted-foreground">
              Drag columns here
            </div>
          )
        ) : (
          <SortableContext
            items={sortableIds}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-1">
              {values.map((value) => {
                const field = lookupField(value.field_id);
                return (
                  <SortableValueItem
                    key={value.field_id}
                    value={value}
                    field={field}
                    onRemove={onRemove}
                    onUpdateFunction={onUpdateFunction}
                    canEdit={canEdit}
                  />
                );
              })}
              {/* Show placeholder at end when dragging from outside */}
              {showPlaceholder && <DropPlaceholder />}
            </div>
          </SortableContext>
        )}
      </div>
    </div>
  );
}
